import { apiPost, apiStore, generateFormData } from '../../../modules/apiHelper';
import { toBoolean, isStaleData, now } from '../../../modules/dataHelper';
import { asyncForEach } from '../../../modules/misc';

export default {
  async AcceptMenteeRequest(context, menteeId) {
    // xhr post
    const index = context.state.menteeRequests.findIndex(m => m.menteeid === menteeId);

    if (index < 0) {
      // [TODO] confirm from db first
      return [false, 'Can\'t accept request at the moment'];
    }
    const mentee = { ...context.state.menteeRequests[index].user };
    // console.log('accepting mentee request... ', menteeId);

    const formData = generateFormData({
      mentor: context.rootState.User.id,
      mentee: menteeId,
      linkid: context.state.menteeRequests[index].linkid,
      groupid: context.state.menteeRequests[index].groupid,
    });

    const response = await apiPost('match', formData, 1)
      .catch((err) => {
        console.warn('Can\'t accept request at the moment', err);
      })
      .then((res) => {
        if (!res) return false;
        if (toBoolean(res.error)) return false;

        context.commit('removeMenteeRequest', formData.get('linkid'));
        // this gets myMentees, new mentee requests
        // and initiates getting chats for myMentees
        context.dispatch('getMyData');

        return true;
      });

    return [
      response,
      response ? `You have now matched with ${mentee.name} ${mentee.middle}` : 'Can\'t accept request at the moment',
    ];
  },
  async DeclineMenteeRequest(context, menteeId) {
    // xhr post
    const index = context.state.menteeRequests.findIndex(m => m.menteeid === menteeId);

    if (index < 0) {
      return [false, 'Can\'t decline request at the moment'];
    }
    // console.log('declining mentee request... ', menteeId);

    const formData = generateFormData({
      mentor: context.rootState.User.id,
      mentee: menteeId,
      groupid: context.state.menteeRequests[index].groupid,
    });

    const response = await apiPost('match_decline', formData, 1)
      .catch((err) => {
        console.warn('Can\'t decline request at the moment', err);
      })
      .then((res) => {
        if (!res) return false;
        if (toBoolean(res.error)) return false;

        context.commit('removeMenteeRequest', context.state.menteeRequests[index].linkid);
        // this gets myMentees, new mentee requests
        // and initiates getting chats for myMentees
        context.dispatch('getMyData');

        return true;
      });

    return [
      response,
      response ? 'Done!' : 'Can\'t decline request at the moment',
    ];
  },
  async ReportMentee(context, { menteeid, reason }) {
    // xhr post
    const Mentee = context.commit('getMentee', menteeid);
    console.log(Mentee, reason);

    context.commit('updateMentee', {
      id: menteeid,
      prop: 'reported',
      value: true,
    });
    return true;
  },
  /**
   * get mentee from store,
   * otherwise get from api
   * then update mentees list at the store
   */
  async getMyMentees(context, refresh = false) {
    const myMentees = await apiStore.getItem('mymentees') || context.state.mentees;

    if (myMentees && !refresh) {
      myMentees.forEach((mentee) => {
        context.commit('addMentee', mentee);
      });
    }

    const formData = new FormData();
    formData.set('mentor', context.rootState.User.id);

    // still fetch then update
    const response = apiPost('matched_mentor', formData, 1)
      .then(async (res) => {
        if (!res) return [];
        if (toBoolean(res.data.error)) return [];

        const mentees = res.data.matched_mentor;

        await mentees.forEach(async (mentee) => {
          // console.log(mentee);
          context.dispatch('getUserData', mentee.menteeid)
            .then(async (u) => {
              if (u) {
                const goals = await context.dispatch('getMenteeGoals', [mentee.menteeid, true]);
                const user = { ...u, goals };
                // console.log(user);
                await context.commit('addMentee', { ...mentee, user });
              }
            });
        });

        return mentees;
      });

    if (!refresh) return myMentees;

    return response;
  },
  async getMenteesRequests(context) {
    const formData = new FormData();
    formData.set('mentor', context.rootState.User.id);

    context.dispatch('fetchNotifications');

    // still fetch then update
    await apiPost('matched_mentor_request', formData, 1)
      .then(async (res) => {
        if (!res) return false;
        if (toBoolean(res.data.error)) return false;

        const mentees = res.data.matched_mentor_request || [];

        await asyncForEach(mentees, async (mentee) => {
          await context.dispatch('getUserData', mentee.menteeid)
            .then(async (u) => {
              if (u) {
                const goals = await context.dispatch('getMenteeGoals', [mentee.menteeid, true]) || [];
                const user = { ...u, goals: goals.map(g => g.goal) };

                context.commit('addMenteeRequest', { ...mentee, user });
              }
            });
        });

        return mentees;
      });
  },

  async createTask(context, [userId, formData, resources = []]) {
    const task = await apiPost('create_tasks', formData, 1)
      .catch((err) => {
        console.warn('Error in creating task \n', err);
        return false;
      })
      .then(async (res) => {
        if (!res) return false;
        if (toBoolean(res.data.error)) return false;

        return res.data;
      });

    let taskResources = [true, 100];
    if (task && task.taskid) {
      taskResources = await context.dispatch('uploadTaskResource', [task.taskid, resources]);
    }

    if (task) {
      await Promise.all([
        context.dispatch('clearReferencedItem', `draftTask/${userId}`),
        context.dispatch('clearReferencedItem', `CreateTaskRefResources/${userId}`),
      ]);
    }

    return [task, ...taskResources];
  },

  async getTaskDraft(context, userId) {
    const [expiry, draft] = await apiStore.getItem(`draftTask/${userId}`) || [0, [undefined, undefined, undefined, undefined]];

    // expiry of 12 hours
    if (isStaleData(expiry, now(), 43200000)) {
      await apiStore.removeItem(`draftTask/${userId}`);
      return [undefined, undefined, undefined, undefined];
    }

    return draft;
  },

  async getCreateTaskReferencedItem(context, refId) {
    const refItem = await apiStore.getItem(refId);
    return refItem;
  },

  async saveCreateTaskDraft(context, [refId, draftData]) {
    const refItem = await apiStore.setItem(`draftTask/${refId}`, [now(), draftData]);
    return refItem;
  },

  async approveTaskReport(context, taskId) {
    const formData = new FormData();
    formData.set('taskid', taskId);

    const response = await apiPost('mark_task_complete', formData, 1)
      .catch((err) => {
        console.warn('acceptTaskReport', err);
        return false;
      })
      .then(async (res) => {
        if (!res) return false;
        if (toBoolean(res.data.error)) return false;

        // get the updated task Details
        context.dispatch('getTask', [taskId, true]);

        return true;
      });

    return response;
  },
  async rejectTaskReport(context, taskId) {
    const formData = new FormData();
    formData.set('taskid', taskId);

    const response = await apiPost('reject_task_report', formData, 1)
      .catch((err) => {
        console.warn('rejectTaskReport', err);
        return false;
      })
      .then(async (res) => {
        if (!res) return false;
        if (toBoolean(res.data.error)) return false;

        // get the updated task Details
        context.dispatch('getTask', [taskId, true]);

        return true;
      });

    return response;
  },
};
